import type { Goal } from '@qi/shared';
import { Button } from '@qi/ui';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Lock, Star } from 'lucide-react';
import { useState } from 'react';
import { useSessionStore } from '../stores/session-store.ts';

export const Route = createFileRoute('/_funnel/start/email')({
  beforeLoad: () => {
    // The email gate sits after capture — bounce back to the start of the
    // funnel if the user landed here without a completed face + voice read.
    const { faceSummary, voiceSummary } = useSessionStore.getState();
    if (!faceSummary || !voiceSummary) throw redirect({ to: '/start' });
  },
  component: FunnelEmailPage,
});

interface Review {
  quote: string;
  name: string;
  meta: string;
}

const STAR_SLOTS = ['s1', 's2', 's3', 's4', 's5'] as const;

// Goal-keyed social proof. Paraphrased, first-name-only — honest framing for a
// pre-launch product, mirroring the landing page's approach. Goals without a
// tailored set fall back to the stress reviews.
const REVIEWS: Partial<Record<Goal, Review[]>> = {
  stress: [
    {
      quote: 'My shoulders dropped about ninety seconds in. I didn’t expect to feel it that fast.',
      name: 'Renee',
      meta: '3 weeks in',
    },
    {
      quote:
        'The calm read was higher than I felt — turns out I was holding more tension than I knew.',
      name: 'Marcus',
      meta: '2 weeks in',
    },
  ],
  sleep: [
    {
      quote: 'I run a session before bed now. Falling asleep stopped being a fight.',
      name: 'Priya',
      meta: '4 weeks in',
    },
    {
      quote: 'Seeing my numbers move night to night kept me coming back.',
      name: 'Tom',
      meta: '5 weeks in',
    },
  ],
  focus: [
    {
      quote: 'Ten minutes before deep work and my head is just… quieter.',
      name: 'Devon',
      meta: '3 weeks in',
    },
    {
      quote: 'The focus score matched my best days. Now I know what to chase.',
      name: 'Aiko',
      meta: '2 weeks in',
    },
  ],
  recovery: [
    {
      quote: 'After training I use it to come down. Recovery felt real, not just a number.',
      name: 'Luis',
      meta: '6 weeks in',
    },
    {
      quote: 'My read climbed as the week went on. First time I could see myself bounce back.',
      name: 'Hannah',
      meta: '4 weeks in',
    },
  ],
  energy: [
    {
      quote: 'The afternoon slump used to own me. A short session resets the whole day.',
      name: 'Sam',
      meta: '3 weeks in',
    },
    {
      quote: 'My energy read was lower than I admitted. Watching it lift kept me honest.',
      name: 'Bea',
      meta: '5 weeks in',
    },
  ],
  meditation: [
    {
      quote: 'It gave my practice a mirror. I can finally see when I actually settle.',
      name: 'Noah',
      meta: '4 weeks in',
    },
    {
      quote: 'The before/after made something invisible feel measurable. I’m hooked.',
      name: 'Mira',
      meta: '7 weeks in',
    },
  ],
};

function FunnelEmailPage() {
  const navigate = useNavigate();
  const goal = useSessionStore((s) => s.goal);
  const [email, setEmail] = useState('');
  const reviews = (goal && REVIEWS[goal]) ?? REVIEWS.stress ?? [];
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid) return;
    // Mock capture — persisted locally until the real list/auth wiring lands.
    try {
      localStorage.setItem('qi.funnel.email', email);
    } catch {
      // Storage blocked (private mode) — proceed regardless; it's non-critical.
    }
    navigate({ to: '/start/scores' });
  }

  return (
    <div className="mx-auto max-w-md space-y-8 px-6 py-12">
      <header className="space-y-3 text-center">
        <p className="font-mono text-xs uppercase tracking-[0.32em] text-aurora">
          Your read is ready
        </p>
        <h1 className="font-display text-3xl text-ink-0">Where should we send your results?</h1>
        <p className="text-pretty text-ink-1">
          Enter your email to unlock your resonance read and keep it so you can pick it back up
          anytime.
        </p>
      </header>

      <form onSubmit={submit} className="space-y-3">
        <input
          type="email"
          required
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          aria-label="Email address"
          className="w-full rounded-md border border-hairline bg-surface-2 px-4 py-3 text-ink-0 placeholder:text-ink-3 focus-visible:border-aurora/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aurora/40"
        />
        <Button type="submit" variant="resonance" size="lg" className="w-full" disabled={!valid}>
          Show my results
        </Button>
        <p className="flex items-center justify-center gap-1.5 text-xs text-ink-3">
          <Lock size={12} strokeWidth={2} className="text-jade" aria-hidden="true" />
          No spam. One-tap unsubscribe. We never sell your data.
        </p>
      </form>

      <div className="space-y-3 border-t border-hairline pt-6">
        {reviews.map((r) => (
          <figure key={r.name} className="qi-glass rounded-lg p-4">
            <div className="mb-1.5 flex gap-0.5 text-aurora" aria-hidden="true">
              {STAR_SLOTS.map((slot) => (
                <Star key={`${r.name}-${slot}`} size={13} strokeWidth={1.6} fill="currentColor" />
              ))}
            </div>
            <blockquote className="text-sm leading-relaxed text-ink-0">"{r.quote}"</blockquote>
            <figcaption className="mt-2 font-mono text-[11px] uppercase tracking-[0.14em] text-ink-3">
              {r.name} · {r.meta}
            </figcaption>
          </figure>
        ))}
        <p className="text-center font-mono text-[11px] uppercase tracking-[0.14em] text-ink-3">
          Paraphrased from early-user feedback
        </p>
      </div>
    </div>
  );
}
